import { useEffect, useState } from "react";
import { Mail, X, Loader2, AlertCircle, Send, Users } from "lucide-react";
import { emailResolution, emailStatus, emailResolutionToMembers } from "@/lib/api";
import { toast } from "sonner";

const DOC_OPTIONS = [
  { key: "resolution", label: "Board Resolution" },
  { key: "shareholder_resolution", label: "Shareholder Resolution" },
  { key: "notice", label: "Notice" },
  { key: "minutes", label: "Minutes" },
];
const FORMAT_OPTIONS = [
  { key: "pdf", label: "PDF" },
  { key: "docx", label: "Word" },
];

export default function EmailModal({ open, onClose, resolution, company }) {
  const isGeneralMeeting = ["agm", "egm"].includes(resolution?.meeting_type);
  const membersWithEmail = (company?.members || []).filter((m) => (m?.email || "").trim());

  const [mode, setMode] = useState("custom"); // 'custom' | 'members'
  const [recipients, setRecipients] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [docTypes, setDocTypes] = useState(["resolution"]);
  const [formats, setFormats] = useState(["pdf"]);
  const [sending, setSending] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [sender, setSender] = useState("");

  useEffect(() => {
    if (open) {
      emailStatus().then((r) => {
        setConfigured(r.data?.configured);
        setSender(r.data?.sender || "");
      });
      setSubject(`Board Resolution ${resolution?.resolution_number || ""} — ${resolution?.resolution_title || ""}`);
      setMode(isGeneralMeeting && membersWithEmail.length > 0 ? "members" : "custom");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resolution]);

  if (!open) return null;

  const toggle = (val, setter, list) => {
    setter(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);
  };

  const sendCustom = async () => {
    const recs = recipients.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    const ccs = cc.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (recs.length === 0) { toast.error("Enter at least one recipient"); return; }
    if (docTypes.length === 0) { toast.error("Select at least one document"); return; }
    if (formats.length === 0) { toast.error("Select at least one format"); return; }

    setSending(true);
    try {
      const res = await emailResolution(resolution.id, {
        recipients: recs,
        cc: ccs,
        subject,
        message,
        doc_types: docTypes,
        formats,
      });
      toast.success(`Sent to ${res.data.recipients.length} recipient(s)`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendToMembers = async () => {
    if (membersWithEmail.length === 0) {
      toast.error("No members with email addresses on this company");
      return;
    }
    setSending(true);
    try {
      const res = await emailResolutionToMembers(resolution.id);
      const sentCount = res.data?.sent || 0;
      const failed = res.data?.failures || [];
      if (sentCount > 0) toast.success(`Notice + MGT-11 sent to ${sentCount} member(s)`);
      if (failed.length > 0) toast.error(`${failed.length} member(s) failed: ${failed.map(f => f.email).join(", ")}`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk send failed");
    } finally {
      setSending(false);
    }
  };

  const send = mode === "members" ? sendToMembers : sendCustom;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="email-modal" onClick={() => onClose(false)}>
      <div className="bg-white border border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0px 0px #0A0A0A" }}>
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[#002FA7]" />
            <h2 className="font-heading text-xl font-medium tracking-tight">Email Resolution</h2>
          </div>
          <button onClick={() => onClose(false)} className="text-zinc-400 hover:text-zinc-900" data-testid="email-modal-close">
            <X size={18} />
          </button>
        </div>

        {!configured && (
          <div className="m-5 p-4 border border-orange-400 bg-orange-50 text-sm flex gap-3" data-testid="email-not-configured">
            <AlertCircle size={16} className="text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Email service not configured.</strong> Add <code className="bg-white px-1">RESEND_API_KEY</code> (or SMTP creds) to <code className="bg-white px-1">/app/backend/.env</code> and restart the backend. Get a free key at <a className="text-[#002FA7] underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a>.
            </div>
          </div>
        )}

        {isGeneralMeeting && (
          <div className="m-5 mb-0">
            <div className="label-eyebrow block mb-1.5">Send Mode</div>
            <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200" data-testid="email-mode-tabs">
              <button
                onClick={() => setMode("members")}
                className={`p-3 text-left text-sm bg-white ${mode === "members" ? "ring-2 ring-[#002FA7] ring-inset" : ""}`}
                data-testid="email-mode-members"
              >
                <div className="flex items-center gap-2 font-medium">
                  <Users size={14} className="text-[#002FA7]" />
                  All Members ({membersWithEmail.length})
                </div>
                <p className="text-xs text-zinc-500 mt-1">Bulk: AGM/EGM Notice + MGT-11 Proxy Form to every shareholder.</p>
              </button>
              <button
                onClick={() => setMode("custom")}
                className={`p-3 text-left text-sm bg-white ${mode === "custom" ? "ring-2 ring-[#002FA7] ring-inset" : ""}`}
                data-testid="email-mode-custom"
              >
                <div className="flex items-center gap-2 font-medium">
                  <Mail size={14} className="text-[#002FA7]" />
                  Custom Recipients
                </div>
                <p className="text-xs text-zinc-500 mt-1">Choose specific recipients and documents.</p>
              </button>
            </div>
          </div>
        )}

        {mode === "members" ? (
          <div className="p-5 space-y-4" data-testid="email-members-panel">
            <Field label="From">
              <div className="text-sm font-mono-plex text-zinc-700">{sender || "—"}</div>
            </Field>
            <Field label={`Recipients (${membersWithEmail.length} members)`}>
              {membersWithEmail.length === 0 ? (
                <div className="p-3 border border-orange-300 bg-orange-50 text-xs flex gap-2 items-center" data-testid="email-no-members">
                  <AlertCircle size={14} className="text-orange-600" />
                  No members with email on this company. Edit the company to add member emails first.
                </div>
              ) : (
                <ul className="max-h-40 overflow-y-auto border border-zinc-200 divide-y divide-zinc-100" data-testid="email-members-list">
                  {membersWithEmail.map((m) => (
                    <li key={m.email} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                      <span className="font-medium text-zinc-800">{m.name || "—"}</span>
                      <span className="font-mono-plex text-zinc-500 truncate">{m.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Field>
            <div className="p-3 border border-zinc-200 bg-zinc-50 text-xs leading-relaxed">
              <div className="font-medium text-zinc-800 mb-1">Will attach:</div>
              <ul className="list-disc list-inside text-zinc-700 space-y-0.5">
                <li>Notice of {resolution?.meeting_type === "agm" ? "Annual General Meeting" : "Extra-Ordinary General Meeting"} (PDF + Word)</li>
                <li>Form MGT-11 — Proxy Form (PDF)</li>
              </ul>
              <p className="mt-2 text-zinc-500">Subject and body are auto-drafted per SS-2.</p>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <Field label="From">
              <div className="text-sm font-mono-plex text-zinc-700">{sender || "onboarding@resend.dev"}</div>
            </Field>

            <Field label="Recipients (comma / newline separated) *">
              <textarea
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                rows={2}
                placeholder="director1@example.com, director2@example.com"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="email-recipients"
              />
            </Field>

            <Field label="CC (optional)">
              <input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cs@example.com, ca@example.com"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="email-cc"
              />
            </Field>

            <Field label="Subject">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="email-subject"
              />
            </Field>

            <Field label="Message (optional)">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Please find attached the board resolution for your reference and signature…"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="email-message"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Documents to attach *">
                <div className="space-y-2">
                  {DOC_OPTIONS.map((d) => {
                    const checked = docTypes.includes(d.key);
                    const disabled =
                      (d.key === "notice" && !resolution?.notice_body) ||
                      (d.key === "minutes" && !resolution?.minutes_body) ||
                      (d.key === "shareholder_resolution" && !resolution?.shareholder_resolution_body);
                    return (
                      <label key={d.key} className={`flex items-center gap-2 text-sm ${disabled ? "opacity-40" : ""}`}>
                        <input
                          type="checkbox"
                          checked={checked && !disabled}
                          disabled={disabled}
                          onChange={() => toggle(d.key, setDocTypes, docTypes)}
                          className="accent-[#002FA7]"
                          data-testid={`email-doc-${d.key}`}
                        />
                        {d.label} {disabled && <span className="text-xs">(not generated)</span>}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Formats *">
                <div className="space-y-2">
                  {FORMAT_OPTIONS.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formats.includes(f.key)}
                        onChange={() => toggle(f.key, setFormats, formats)}
                        className="accent-[#002FA7]"
                        data-testid={`email-format-${f.key}`}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 p-5 border-t border-zinc-200">
          <button onClick={() => onClose(false)} className="swiss-btn-ghost" data-testid="email-cancel">Cancel</button>
          <button
            onClick={send}
            disabled={sending || !configured || (mode === "members" && membersWithEmail.length === 0)}
            className="swiss-btn-primary"
            data-testid="email-send"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : (mode === "members" ? <Users size={14} /> : <Send size={14} />)}
            {sending ? "Sending…" : (mode === "members" ? `Send to ${membersWithEmail.length} Members` : "Send Email")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
